import React from "react";
import { Box, Typography, IconButton, Tooltip, Checkbox } from "@mui/material";
import SnoozeIcon from "@mui/icons-material/Snooze";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import FlagIcon from "@mui/icons-material/Flag";
import OutlinedFlagIcon from "@mui/icons-material/OutlinedFlag";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import { ExpensePriority } from "../../lib/types";
import { formatCurrency } from "../../lib/expenseUtils";
import {
  RecurringBillLine,
  PriorityFilterValue,
} from "../../lib/recurringBillsUtils";

export const PRIORITY_LABEL: Record<PriorityFilterValue, string> = {
  emergency: "Emergency",
  essential: "Essential",
  optional: "Optional",
  unassigned: "Unassigned",
};

// Priority choices for the hover picker, in severity order.
export const PRIORITY_CHOICES: {
  value: ExpensePriority;
  label: string;
  color: string;
}[] = [
  { value: "emergency", label: "Emergency", color: "error.main" },
  { value: "essential", label: "Essential", color: "warning.main" },
  { value: "optional", label: "Optional", color: "success.main" },
];

// A little flag whose color is the current priority. Hovering slides out the
// options (which bounce in); clicking one sets it, clicking the current one
// clears it.
export const PriorityPicker: React.FC<{
  value: PriorityFilterValue;
  onSelect: (p: ExpensePriority | null) => void;
}> = ({ value, onSelect }) => {
  const [hover, setHover] = React.useState(false);
  const current = PRIORITY_CHOICES.find((c) => c.value === value);
  return (
    <Box
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}
    >
      <Tooltip title={current ? current.label : "Set priority"}>
        {current ? (
          <FlagIcon fontSize="small" sx={{ color: current.color }} />
        ) : (
          <OutlinedFlagIcon fontSize="small" sx={{ color: "text.disabled" }} />
        )}
      </Tooltip>
      {hover && (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.25,
            "@keyframes flagBounceIn": {
              "0%": { transform: "scale(0)", opacity: 0 },
              "60%": { transform: "scale(1.3)" },
              "100%": { transform: "scale(1)", opacity: 1 },
            },
          }}
        >
          {PRIORITY_CHOICES.map((c, i) => (
            <Tooltip key={c.value} title={c.label}>
              <IconButton
                size="small"
                onClick={() => onSelect(value === c.value ? null : c.value)}
                sx={{
                  p: 0.25,
                  color: c.color,
                  opacity: value === c.value ? 1 : 0.85,
                  animation: "flagBounceIn 260ms ease",
                  animationDelay: `${i * 60}ms`,
                  animationFillMode: "both",
                }}
              >
                <FlagIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ))}
        </Box>
      )}
    </Box>
  );
};

export interface RecurringBillRowProps {
  line: RecurringBillLine;
  // Amount to display on the right (native charge, monthly-equivalent, etc.).
  displayAmount: number;
  hidden: boolean;
  onToggleExcluded: (id: string) => void;
  showPostpone: boolean;
  // Opens the postpone popover anchored to the snooze button.
  onRequestPostpone?: (line: RecurringBillLine, anchorEl: HTMLElement) => void;
  onSetExpensePriority?: (
    expenseId: string,
    priority: ExpensePriority | null
  ) => void;
  onEditBill?: (id: string) => void;
  onDeleteBill?: (id: string) => void;
  onEditDebt?: (debtId: string) => void;
  onDeleteDebt?: (debtId: string) => void;
  // Optional drag handle rendered at the far left (priority board only).
  dragHandle?: React.ReactNode;
}

/**
 * One recurring-bill / debt row with the full feature set: hide-from-totals
 * checkbox, name + subtitle, debt-link icon, priority flag picker, postponed
 * indicator, amount, snooze, edit, delete. Shared by the cadence list and the
 * drag-to-rank priority board so both look and behave identically.
 */
const RecurringBillRow: React.FC<RecurringBillRowProps> = ({
  line,
  displayAmount,
  hidden,
  onToggleExcluded,
  showPostpone,
  onRequestPostpone,
  onSetExpensePriority,
  onEditBill,
  onDeleteBill,
  onEditDebt,
  onDeleteDebt,
  dragHandle,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 0.25,
        gap: 1,
        opacity: hidden ? 0.4 : 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          minWidth: 0,
        }}
      >
        {dragHandle}
        <Tooltip title={hidden ? "Include in totals" : "Hide from totals"}>
          <Checkbox
            size="small"
            checked={!hidden}
            onChange={() => onToggleExcluded(line.id)}
            sx={{ p: 0.25 }}
          />
        </Tooltip>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" noWrap>
            {line.name}
          </Typography>
          {line.subName && (
            <Typography
              variant="caption"
              noWrap
              sx={{
                display: "block",
                color: "text.disabled",
                fontSize: "0.7rem",
                lineHeight: 1.1,
              }}
            >
              {line.subName}
            </Typography>
          )}
        </Box>
        {line.kind === "debt" && (
          <Tooltip title="Debt">
            <CreditCardIcon fontSize="small" sx={{ color: "text.secondary" }} />
          </Tooltip>
        )}
        {line.kind === "bill" && line.linkedDebtId && (
          <Tooltip title="Debt payment — click to open the debt">
            <IconButton
              size="small"
              sx={{ p: 0 }}
              onClick={() =>
                onEditDebt && onEditDebt(line.linkedDebtId as string)
              }
            >
              <CreditCardIcon fontSize="small" sx={{ color: "info.main" }} />
            </IconButton>
          </Tooltip>
        )}
        {line.kind === "bill" && onSetExpensePriority ? (
          <PriorityPicker
            value={line.priority}
            onSelect={(p) => onSetExpensePriority(line.id, p)}
          />
        ) : (
          line.priority !== "unassigned" && (
            <Tooltip title={PRIORITY_LABEL[line.priority]}>
              <FlagIcon
                fontSize="small"
                sx={{
                  color:
                    PRIORITY_CHOICES.find((c) => c.value === line.priority)
                      ?.color || "text.disabled",
                }}
              />
            </Tooltip>
          )
        )}
        {line.isPostponed && (
          <Tooltip title="Postponed">
            <PauseCircleOutlineIcon
              fontSize="small"
              sx={{ color: "text.secondary" }}
            />
          </Tooltip>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
        <Typography
          variant="body2"
          sx={{ whiteSpace: "nowrap", textAlign: "right", ml: 1 }}
        >
          {formatCurrency(displayAmount)}
        </Typography>
        {showPostpone && onRequestPostpone && (
          <Tooltip title={line.isPostponed ? "Postponed" : "Postpone"}>
            <IconButton
              size="small"
              color={line.isPostponed ? "warning" : "primary"}
              onClick={(e) => onRequestPostpone(line, e.currentTarget)}
            >
              <SnoozeIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        )}
        {line.kind === "bill" && onEditBill && (
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => onEditBill(line.id)}>
              <EditIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        )}
        {line.kind === "debt" && onEditDebt && (
          <Tooltip title="Edit debt">
            <IconButton
              size="small"
              onClick={() => onEditDebt(line.id.replace(/^debt-/, ""))}
            >
              <EditIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        )}
        {line.kind === "bill" && onDeleteBill && (
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => onDeleteBill(line.id)}>
              <DeleteOutlineIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        )}
        {line.kind === "debt" && onDeleteDebt && (
          <Tooltip title="Delete debt">
            <IconButton
              size="small"
              onClick={() => onDeleteDebt(line.id.replace(/^debt-/, ""))}
            >
              <DeleteOutlineIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default RecurringBillRow;
